"use client"

/**
 * Overseeding calculator island — ported from the Claude Design spec
 * (project 68b86b0d, "Overseeding Calculator.dc.html"). Inline-styled to match
 * the design; wired to the real `calculateOverseeding` engine and region
 * resolver. Fully live (no submit gate), like sibling tools.
 *
 * The evergreen SEO sections (rate table, overseeding-vs-reseeding, how-to,
 * FAQ) are NOT here — they render server-side via ToolContentFooter so they stay
 * crawlable (this island sits behind a useSearchParams Suspense boundary and
 * never reaches the initial HTML).
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useBottomDockInset } from "@/hooks/use-bottom-dock-inset"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"
import {
  MapPin,
  ArrowDown,
  Check,
  CheckCircle2,
  Timer,
  CalendarClock,
  CalendarX,
  Info,
  Snowflake,
  AlertTriangle,
  Scale,
  TrendingDown,
  LayoutDashboard,
  type LucideIcon,
} from "lucide-react"
import {
  calculateOverseeding,
  OVERSEED_GRASS_OPTIONS,
  type OverseedGrassId,
  type OverseedingResult,
  type OverseedWindow,
  type LawnCondition,
  type WindowState,
} from "@/lib/overseeding-calculator"
import { buildToolResultPick } from "@/lib/tool-result-pick"
import { ToolResultPick } from "@/components/tools/shared/tool-result-pick"
import { handOffToResult } from "@/components/tools/shared/use-result-handoff"

const SIZE_PRESETS = [2500, 5000, 10000, 20000]
const AREA_MIN = 200
const AREA_MAX = 30000

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
// Local, tz-safe formatter (no toLocaleDateString → no hydration mismatch).
function monthDay(d: Date): string {
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`
}
export function rangeLabel(w: OverseedWindow): string {
  return `${monthDay(w.startDate)} – ${monthDay(w.endDate)}`
}

const CONDITIONS: { id: LawnCondition; label: string; sub: string; icon: LucideIcon }[] = [
  { id: "thin", label: "A little thin", sub: "Mostly green, wants density", icon: TrendingDown },
  { id: "patchy", label: "Patchy", sub: "Visible bare spots", icon: LayoutDashboard },
  { id: "mostly-bare", label: "Mostly bare", sub: "More dirt than grass", icon: AlertTriangle },
]

const WINDOW_TAG: Record<WindowState, { color: string; bg: string }> = {
  active: { color: "#fff", bg: "#16a34a" },
  closing: { color: "#92400e", bg: "#fde68a" },
  upcoming: { color: "#166534", bg: "#dcfce7" },
  passed: { color: "#6b7280", bg: "#f3f4f6" },
}

export interface OverseedingInitialValues {
  zipCode?: string
  grassType?: OverseedGrassId
  lawnSize?: number
}

export function OverseedingForm({
  initialValues,
  onResultChange,
  resultTargetId,
}: {
  initialValues?: OverseedingInitialValues | null
  /** Receives the live result plus the lawn size it was computed for. */
  onResultChange?: (result: OverseedingResult, lawnSizeSqFt: number) => void
  /** Id of the page's result block. While it renders, the rail shows a compact
   *  readout and the dock hands off to that block instead. */
  resultTargetId?: string
}) {
  const [zip, setZip] = useState(initialValues?.zipCode ?? "")
  const [grass, setGrass] = useState<OverseedGrassId>(initialValues?.grassType ?? "fescue")
  const [area, setArea] = useState(initialValues?.lawnSize ?? 5000)
  const [condition, setCondition] = useState<LawnCondition>("thin")
  const [coated, setCoated] = useState(false)
  const [narrow, setNarrow] = useState(false)
  // Keeps the mobile dock above the Mediavine anchor ad pinned beneath it.
  const dockInset = useBottomDockInset()

  const rootRef = useRef<HTMLDivElement | null>(null)
  const railRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!initialValues) return
    if (initialValues.zipCode) setZip(initialValues.zipCode)
    if (initialValues.grassType) setGrass(initialValues.grassType)
    if (typeof initialValues.lawnSize === "number") setArea(initialValues.lawnSize)
  }, [initialValues])

  useEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(() => setNarrow(el.getBoundingClientRect().width < 720))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const result = useMemo(
    () =>
      calculateOverseeding({
        zipCode: zip,
        grassType: grass,
        lawnSizeSqFt: area,
        seedType: coated ? "coated" : "uncoated",
        lawnCondition: condition,
      }),
    [zip, grass, area, condition, coated],
  )

  useEffect(() => {
    onResultChange?.(result, area)
  }, [result, area, onResultChange])

  const region = result.regionKnown ? result.region : null
  const verdict = buildVerdict(result)
  const stolonsOnly = result.mode === "stolons-only"
  const resultPick = useMemo(
    () =>
      buildToolResultPick({
        toolSlug: "overseeding-calculator",
        product: result.product
          ? {
              name: result.product.name,
              affiliateUrl: result.product.url,
              imageUrl: result.product.imageUrl,
              reason: result.product.why,
            }
          : null,
        totalLbs: result.seed?.lbsRecommended,
      }),
    [result.product, result.seed?.lbsRecommended],
  )

  // Mirrors the page's success-card gate: a known region plus a seed or plug figure.
  const peakShown = Boolean(resultTargetId) && result.regionKnown && Boolean(result.seed || (stolonsOnly && result.plugTrays != null))

  const scrollToResults = () => {
    const peak = peakShown && resultTargetId ? document.getElementById(resultTargetId) : null
    const target = peak ?? railRef.current
    if (target) handOffToResult(target)
  }

  const dockAmount = stolonsOnly
    ? `${result.plugTrays} plug tray${result.plugTrays === 1 ? "" : "s"}`
    : result.seed
      ? `${fmtLbs(result.seed.lbsRecommended)} lbs · ${result.seed.bagCombo.split(" + ")[0]}`
      : result.seedGuidance ? "Check seed label" : "—"
  const dockWindow = verdict.eyebrow

  return (
    <div ref={rootRef}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fit, minmax(330px, 1fr))",
          gap: 28,
          alignItems: "start",
        }}
      >
        {/* ============ FORM ============ */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-elevation-2 sm:p-7">
          <FieldLabel>1 · Your ZIP code</FieldLabel>
          <div style={{ position: "relative" }}>
            <MapPin
              size={18}
              style={{ position: "absolute", top: "50%", left: 16, transform: "translateY(-50%)", color: "#9ca3af" }}
            />
            <input
              {...ZIP_INPUT_ATTRS}
              value={zip}
              onChange={(e) => setZip(sanitizeZip(e.target.value))}
              placeholder="e.g. 44107"
              aria-label="ZIP code"
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 130px 0 44px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 14,
                transform: "translateY(-50%)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 12px",
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 700,
                color: region ? "#166534" : "#6b7280",
                background: region ? "#f0fdf4" : "#f9fafb",
                border: `1px solid ${region ? "#bbf7d0" : "#e5e7eb"}`,
              }}
            >
              {region ?? "Enter ZIP"}
            </span>
          </div>

          <FieldLabel style={{ marginTop: 24 }}>2 · Grass type</FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {OVERSEED_GRASS_OPTIONS.map((g) => {
              const selected = g.id === grass
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGrass(g.id)}
                  aria-pressed={selected}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    minHeight: 44,
                    padding: "0 16px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 9999,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 14,
                    fontWeight: 700,
                    color: selected ? "#166534" : "#374151",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                >
                  {g.label}
                </button>
              )
            })}
          </div>

          <FieldLabel style={{ marginTop: 24 }}>3 · Lawn size</FieldLabel>
          <div style={{ position: "relative" }}>
            <input
              type="number"
              value={area}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                setArea(isNaN(v) ? 0 : Math.max(0, v))
              }}
              aria-label="Lawn size in square feet"
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 74px 0 16px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 16,
                transform: "translateY(-50%)",
                fontSize: 13,
                fontWeight: 600,
                color: "#6b7280",
              }}
            >
              sq ft
            </span>
          </div>
          <input
            type="range"
            min={Math.log10(AREA_MIN)}
            max={Math.log10(AREA_MAX)}
            step={0.01}
            value={Math.log10(Math.min(Math.max(area, AREA_MIN), AREA_MAX))}
            onChange={(e) => setArea(Math.round(Math.pow(10, parseFloat(e.target.value)) / 100) * 100)}
            aria-label="Lawn size slider"
            style={{ width: "100%", marginTop: 12, accentColor: "#16a34a" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {SIZE_PRESETS.map((v) => {
              const selected = v === area
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setArea(v)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    minHeight: 44,
                    padding: "0 14px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 9999,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#111827",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                >
                  {v.toLocaleString()} sq ft
                </button>
              )
            })}
          </div>

          <FieldLabel style={{ marginTop: 24 }}>4 · How does your lawn look?</FieldLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {CONDITIONS.map((c) => {
              const selected = c.id === condition
              const Icon = c.icon
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCondition(c.id)}
                  aria-pressed={selected}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    boxSizing: "border-box",
                    padding: "14px 8px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 16,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "center",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                >
                  <Icon size={22} style={{ color: selected ? "#16a34a" : "#9ca3af" }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{c.label}</span>
                  <span style={{ fontSize: 11, lineHeight: 1.35, color: "#6b7280" }}>{c.sub}</span>
                </button>
              )
            })}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginTop: 20,
              padding: "14px 16px",
              borderRadius: 14,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
            }}
          >
            <div>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "#111827" }}>Coated seed?</span>
              <span style={{ display: "block", marginTop: 2, fontSize: 12, color: "#6b7280" }}>
                Coated seed weighs more, so you need about 1.5× the pounds
              </span>
            </div>
            <button
              type="button"
              onClick={() => setCoated((c) => !c)}
              role="switch"
              aria-checked={coated}
              aria-label="Coated seed"
              style={{
                position: "relative",
                flexShrink: 0,
                width: 52,
                height: 30,
                border: "none",
                borderRadius: 9999,
                cursor: "pointer",
                background: coated ? "#16a34a" : "#d1d5db",
                transition: "background 200ms ease",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  left: coated ? 25 : 3,
                  width: 24,
                  height: 24,
                  borderRadius: 9999,
                  background: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  transition: "left 200ms ease",
                }}
              />
            </button>
          </div>
        </div>

        {/* ============ RESULTS RAIL ============ */}
        <div
          ref={railRef}
          className="scroll-mt-20"
          style={{
            position: narrow ? "static" : "sticky",
            top: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* verdict callout */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              borderRadius: 20,
              padding: "18px 22px",
              background: verdict.bg,
              color: verdict.color,
              border: `1px solid ${verdict.border}`,
              boxShadow: verdict.tone === "go" ? "0 8px 20px -6px rgba(22,163,74,0.18)" : "none",
            }}
          >
            <verdict.icon size={26} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", opacity: 0.8 }}>
                {verdict.eyebrow}
              </div>
              <div style={{ marginTop: 3, fontSize: 22, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
                {verdict.title}
              </div>
              <div style={{ marginTop: 3, fontSize: 13, fontWeight: 600, opacity: 0.85 }}>{verdict.sub}</div>
            </div>
          </div>

          {/* stolons-only (St. Augustine) */}
          {stolonsOnly && (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-elevation-2">
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <Info size={22} style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }} />
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#111827" }}>
                    St. Augustine doesn&apos;t overseed from a bag
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.6, color: "#374151" }}>
                    Commercial St. Augustine seed isn&apos;t viable, so thin areas are repaired with plugs, sprigs, or sod.
                    Plan on one tray of plugs per 32 sq ft of thin turf, planted 12 inches apart in late spring through
                    mid-summer.
                  </p>
                </div>
              </div>
              {!peakShown && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
                  <div style={{ padding: 16, borderRadius: 16, background: "#f0fdf4", border: "1px solid #bbf7d0", textAlign: "center" }}>
                    <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: "#14532d" }}>{result.plugTrays}</div>
                    <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: "#166534" }}>
                      plug trays for thin areas (~20% of lawn)
                    </div>
                  </div>
                  <div style={{ padding: 16, borderRadius: 16, background: "#f9fafb", border: "1px solid #e5e7eb", textAlign: "center" }}>
                    <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: "#111827" }}>12 in</div>
                    <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: "#6b7280" }}>
                      plug spacing for fill-in by next season
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* seed result card */}
          {result.seedGuidance && <p role="status">{result.seedGuidance}</p>}
          {!stolonsOnly && result.seed && (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
              <div className="flex items-center justify-between gap-3 bg-green-700 px-6 py-4 text-white">
                <h2 className="flex items-center gap-2.5 text-base font-bold">
                  <Scale size={19} aria-hidden="true" />
                  Your seed order
                </h2>
                <span className="text-sm font-semibold text-green-50">{result.grassLabel}</span>
              </div>

              <div style={{ padding: 24 }}>
                {peakShown ? (
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <p className="text-sm font-semibold text-green-900">
                      <span className="text-xl font-extrabold tabular-nums">{fmtLbs(result.seed.lbsRecommended)} lbs</span>
                      {" · "}
                      {result.seed.bagCombo}
                    </p>
                    {narrow && resultTargetId && (
                      <a
                        href={`#${resultTargetId}`}
                        className="press inline-flex min-h-11 items-center gap-1.5 rounded-xl bg-action px-4 text-sm font-bold text-action-foreground no-underline hover:bg-action-hover"
                      >
                        See my plan
                        <ArrowDown size={15} aria-hidden="true" />
                      </a>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ padding: 22, borderRadius: 18, background: "#f0fdf4", border: "1px solid #bbf7d0", textAlign: "center" }}>
                      <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em", color: "#14532d" }}>
                        {fmtLbs(result.seed.lbsRecommended)} lbs
                      </div>
                      <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: "#166534" }}>
                        of {coated ? "coated " : ""}
                        {result.grassLabel.toLowerCase()} seed for {area.toLocaleString()} sq ft
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                      <StatBox
                        value={String(result.seed.ratePer1000)}
                        label={`lbs per 1,000 sq ft (${CONDITIONS.find((c) => c.id === condition)?.label.toLowerCase()})`}
                      />
                      <StatBox value={result.seed.bagCombo} label={`adds up to ${result.seed.bagsTotalLbs} lbs`} />
                    </div>
                  </>
                )}

                {/* windows */}
                <SectionLabel style={{ marginTop: 22 }}>
                  {region ? `Your windows in the ${region}` : "Your overseeding windows"}
                </SectionLabel>
                {result.windows.length > 0 ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {result.windows.map((w) => {
                      const tag = WINDOW_TAG[w.state]
                      const tagText =
                        w.state === "active"
                          ? "Open now"
                          : w.state === "closing"
                            ? `${w.daysLeft} days left`
                            : w.state === "upcoming"
                              ? `${w.daysUntilStart} days`
                              : "Passed"
                      const active = w.state === "active" || w.state === "closing" || (w.state === "upcoming" && w.isBest)
                      const label = w.isBest
                        ? w.key === "fall"
                          ? "Fall (best)"
                          : "Late spring (best)"
                        : "Spring (backup)"
                      return (
                        <div
                          key={w.key}
                          style={{
                            padding: 16,
                            borderRadius: 16,
                            background: active ? "#f0fdf4" : "#f9fafb",
                            border: `1px solid ${active ? "#bbf7d0" : "#e5e7eb"}`,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6b7280" }}>
                              {label}
                            </span>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 10px",
                                borderRadius: 9999,
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: "0.03em",
                                textTransform: "uppercase",
                                color: tag.color,
                                background: tag.bg,
                              }}
                            >
                              {tagText}
                            </span>
                          </div>
                          <div style={{ marginTop: 8, fontSize: 19, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.01em", color: "#111827" }}>
                            {rangeLabel(w)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: 13.5, color: "#6b7280" }}>
                    Enter a valid US ZIP code to see your overseeding window and countdown.
                  </p>
                )}

                {/* winter ryegrass note */}
                {result.showWinterRyegrass && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      marginTop: 16,
                      padding: "14px 16px",
                      borderRadius: 14,
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <Snowflake size={18} style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }} />
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "#166534" }}>
                      <strong>Want green grass all winter?</strong> Your {result.grassLabel} goes dormant, but you can
                      overseed with perennial ryegrass in Oct–Nov at 5–10 lbs per 1,000 sq ft for winter color. It dies off
                      when summer heat returns.
                    </p>
                  </div>
                )}

                {/* prep checklist */}
                <SectionLabel style={{ marginTop: 22, color: "#6b7280" }}>Set the seed up to win</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {[
                    "Core-aerate first: seed-to-soil contact through thatch roughly doubles germination.",
                    "Mow low (about 2 in) and bag clippings so seed reaches the soil.",
                    "Use a starter fertilizer at seeding, and skip weed control for 4 weeks either side.",
                    "Water lightly 2× a day for 2–3 weeks until the new grass is mowed once.",
                  ].map((tip) => (
                    <div key={tip} style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                      <Check size={15} style={{ flexShrink: 0, marginTop: 2, color: "#16a34a" }} />
                      <span style={{ fontSize: 13.5, lineHeight: 1.5, color: "#374151" }}>{tip}</span>
                    </div>
                  ))}
                </div>

                {/* bare-lawn nudge */}
                {result.showBareNudge && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      marginTop: 16,
                      padding: "14px 16px",
                      borderRadius: 14,
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1, color: "#d97706" }} />
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "#92400e" }}>
                      Mostly bare ground is closer to a new lawn than an overseed. This estimate uses a heavier rate, but the{" "}
                      <a href="/tools/seeding-calculator" style={{ color: "#92400e", fontWeight: 700 }}>
                        seeding calculator
                      </a>{" "}
                      will give you a proper full-seed plan.
                    </p>
                  </div>
                )}

                {/* The grass's seed, sized to the pounds above. Tracked as
                    tool-result:overseeding-calculator; null for a demoted ASIN. */}
                <ToolResultPick pick={resultPick} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* mobile bottom dock */}
      {narrow && (
        <div
          style={{
            position: "sticky",
            bottom: dockInset,
            zIndex: 40,
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginTop: 16,
            padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
            borderTop: "1px solid #e5e7eb",
            boxShadow: "0 -8px 30px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {dockAmount}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>{dockWindow}</div>
          </div>
          <button
            type="button"
            onClick={scrollToResults}
            className="press flex min-h-11 items-center gap-2 whitespace-nowrap rounded-xl bg-action px-5 text-sm font-bold text-action-foreground hover:bg-action-hover"
          >
            See my plan
            <ArrowDown size={15} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  )
}

export function fmtLbs(lbs: number): string {
  return lbs >= 100 ? lbs.toFixed(0) : String(Math.round(lbs * 10) / 10)
}

interface VerdictView {
  eyebrow: string
  title: string
  sub: string
  icon: LucideIcon
  tone: "go" | "warn" | "neutral"
  bg: string
  color: string
  border: string
}

function buildVerdict(result: OverseedingResult): VerdictView {
  const go = { tone: "go" as const, bg: "#15803d", color: "#fff", border: "transparent" }
  const warn = { tone: "warn" as const, bg: "#fffbeb", color: "#92400e", border: "#fde68a" }
  const neutral = { tone: "neutral" as const, bg: "#fff", color: "#111827", border: "#e5e7eb" }

  const focus = result.focusWindow
  if (result.grassId === 'carpetgrass') {
    return { eyebrow: 'Planting conditions', title: 'Check soil temperature', sub: 'Use soil at or above 69°F and follow the seed label.', icon: MapPin, ...neutral }
  }
  if (!result.regionKnown || !focus || !result.verdict) {
    return { eyebrow: "Best window", title: "Enter your ZIP", sub: "We'll show your overseeding window and countdown", icon: MapPin, ...neutral }
  }

  const range = rangeLabel(focus)
  switch (result.verdict) {
    case "in-window":
      return { eyebrow: "You're in the window", title: "Overseed now", sub: `${range} · conditions are right`, icon: CheckCircle2, ...go }
    case "closing-soon":
      return { eyebrow: "Closing soon", title: `${focus.daysLeft} days left in your window`, sub: `Overseed now: ${range}`, icon: Timer, ...go }
    case "upcoming":
      return { eyebrow: `Best window opens in ${focus.daysUntilStart} days`, title: range, sub: "Time to order seed and plan aeration", icon: CalendarClock, ...go }
    case "missed-fall-use-spring":
      return { eyebrow: "This year's fall window has passed", title: `Plan for spring: ${range}`, sub: "Fall is better, but spring works with extra watering", icon: CalendarX, ...warn }
    case "plan-next-window":
      return { eyebrow: "This year's window has passed", title: `Next: ${range}`, sub: "Mark your calendar for next season", icon: CalendarX, ...warn }
  }
}

function StatBox({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ padding: 14, borderRadius: 14, background: "#f9fafb", border: "1px solid #e5e7eb", textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1, color: "#111827" }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: 12, fontWeight: 600, color: "#6b7280" }}>{label}</div>
    </div>
  )
}

function FieldLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#15803d", ...style }}>
      {children}
    </p>
  )
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#15803d", ...style }}>
      {children}
    </p>
  )
}
